/**
 * AES-256-GCM for the `password` fields that are set to encrypted, in the browser with WebCrypto.
 * The derivations are pinned in the `InvPasswordEncrypted` description of the backend (`tools/openinv/definitions.py`).
 *
 * - `every_access`: `PBKDF2-HMAC-SHA256(passphrase, salt, 600000)`, the passphrase is typed every time and never kept.
 * - `session`: `HKDF-SHA256(secret, salt, "ams-password:v1")`, `secret` is the `encryption_key` the server answers when logging in.
 */

export const ALGORITHM = "aes-256-gcm";
export const PBKDF2_ITERATIONS = 600_000;
export const IV_LENGTH = 12;
export const SALT_LENGTH = 16;
export const HKDF_INFO = "ams-password:v1";

/** WebCrypto wants typed arrays that are backed by an `ArrayBuffer`, not a `SharedArrayBuffer`. */
export type Bytes = Uint8Array<ArrayBuffer>;

export type UnlockMode = "session" | "every_access";

/** What is stored as the value of an encrypted password, all binary members are base64. */
export interface EncryptedPassword {
  algorithm: typeof ALGORITHM;
  unlock: UnlockMode;
  ciphertext: string;
  iv: string;
  salt: string;
}

/** Derives the AES key of a value from its salt. */
export type KeySource = (salt: Bytes) => Promise<CryptoKey>;

export function toBase64(bytes: Uint8Array): string {
  let binary = "";
  bytes.forEach((byte) => {
    binary += String.fromCharCode(byte);
  });
  return btoa(binary);
}

export function fromBase64(text: string): Bytes {
  const binary = atob(text);
  const bytes: Bytes = new Uint8Array(binary.length);
  for (let index = 0; index < binary.length; index++) {
    bytes[index] = binary.charCodeAt(index);
  }
  return bytes;
}

/** The unlock mode of an encrypted value, `null` if the value is something else (typed text, an empty value, …). */
export function unlockOf(value: unknown): UnlockMode | null {
  if (typeof value !== "object" || value === null) return null;
  const { algorithm, unlock } = value as Partial<EncryptedPassword>;
  return algorithm === ALGORITHM && (unlock === "session" || unlock === "every_access") ? unlock : null;
}

/** Keeps the secret of the login as a key that cannot be read out again, only used to derive the keys of the values. */
export async function importSessionMaster(secret: string, subtle: SubtleCrypto = crypto.subtle): Promise<CryptoKey> {
  return subtle.importKey("raw", fromBase64(secret), "HKDF", false, ["deriveKey"]);
}

export function sessionKeySource(master: CryptoKey, subtle: SubtleCrypto = crypto.subtle): KeySource {
  return (salt) => subtle.deriveKey(
    { name: "HKDF", hash: "SHA-256", salt, info: new TextEncoder().encode(HKDF_INFO) as Bytes },
    master,
    { name: "AES-GCM", length: 256 },
    false,
    ["encrypt", "decrypt"],
  );
}

export function passphraseKeySource(passphrase: string, subtle: SubtleCrypto = crypto.subtle): KeySource {
  return async (salt) => {
    const material = await subtle.importKey("raw", new TextEncoder().encode(passphrase) as Bytes, "PBKDF2", false, ["deriveKey"]);
    return subtle.deriveKey(
      { name: "PBKDF2", hash: "SHA-256", salt, iterations: PBKDF2_ITERATIONS },
      material,
      { name: "AES-GCM", length: 256 },
      false,
      ["encrypt", "decrypt"],
    );
  };
}

/** `iv` and `salt` are random unless given, which only the tests do. */
export async function encryptPassword(
  plain: string,
  unlock: UnlockMode,
  keySource: KeySource,
  subtle: SubtleCrypto = crypto.subtle,
  random: { iv?: Bytes; salt?: Bytes } = {},
): Promise<EncryptedPassword> {
  const salt = random.salt ?? crypto.getRandomValues(new Uint8Array(SALT_LENGTH) as Bytes);
  const iv = random.iv ?? crypto.getRandomValues(new Uint8Array(IV_LENGTH) as Bytes);
  const key = await keySource(salt);
  const ciphertext = new Uint8Array(await subtle.encrypt({ name: "AES-GCM", iv }, key, new TextEncoder().encode(plain) as Bytes));
  return { algorithm: ALGORITHM, unlock, ciphertext: toBase64(ciphertext), iv: toBase64(iv), salt: toBase64(salt) };
}

/** @throws DOMException `OperationError` for a wrong key or a changed value. */
export async function decryptPassword(
  value: EncryptedPassword,
  keySource: KeySource,
  subtle: SubtleCrypto = crypto.subtle,
): Promise<string> {
  const key = await keySource(fromBase64(value.salt));
  const plain = await subtle.decrypt({ name: "AES-GCM", iv: fromBase64(value.iv) }, key, fromBase64(value.ciphertext));
  return new TextDecoder().decode(plain);
}
